import type { Module } from 'vuex';
import {IWishlistItem, IWishlistParams} from '../../entities/wishlist-item-entity';

export const namespace = 'wishlist';

export type AddItemToWishlistData = {
	id: number;
};

export interface ILoadingState {
	getWishlist: boolean;
	addItem: boolean;
	removeItem: boolean;
}

export interface IWishlistState {
	items: IWishlistItem[];
	loading: ILoadingState;
}

export const MutationType = {
	ADD_TO_WISHLIST: 'addToWishlist',
	REMOVE_ITEM_FROM_WISHLIST: 'removeItemFromWishlist',
	SET_WISHLIST_ITEMS: 'setWishlistItems',
	SET_LOADING: 'setLoading',
};

export const actionType = {
	ADD_TO_WISHLIST: 'addToWishlist',
	REMOVE_ITEM_FROM_WISHLIST: 'removeItem',
	GET_WISHLIST: 'getWishlist',
	CLEAR_WISHLIST: 'clearWishlist',
};

export default function wishlistStore(): Module<IWishlistState, any> {
	return {
		namespaced: true,
		state: () => ({
			items: [],
			loading: {
				getWishlist: false,
				addItem: false,
				removeItem: false,
			},
		}),
		actions: {
			async [actionType.GET_WISHLIST]({ commit }, params: IWishlistParams): Promise<IWishlistItem[]> {
				try {
					commit('appState/setLoading', { wishlist: true }, { root: true });

					const res = await this.$api.wishlist.getWishlist.execute({
						extensions: params?.extensions || []
					});

					if (res.isSuccess) {
						const value = res.getValue();
						commit(MutationType.SET_WISHLIST_ITEMS, value?.wishlistItems);
						return value?.wishlistItems;
					}
					return [];
				} finally {
					commit('appState/setLoading', { wishlist: false }, { root: true });
				}
			},
			async [actionType.ADD_TO_WISHLIST]({ commit, dispatch }, id: number): Promise<boolean> {
				try {
					commit('appState/setLoading', { wishlistAddItem: true }, { root: true });
					const res = await this.$api.wishlist.addWishlistItem.execute({ wishlistItemId: id });
					if (res.isSuccess) {
						const value = res.getValue();
						if (value?.sessionToken) {
							const sessionCookie = this.$cookies.get('sessionToken');

							if (!sessionCookie) {
								this.$cookies.set('sessionToken', value.sessionToken);
							}
							dispatch('userAuth/SET_SESSION_TOKEN', value.sessionToken, { root: true });
						}
						commit(MutationType.ADD_TO_WISHLIST, value?.wishlistItem);
					}
					return res.isSuccess as boolean;
				} catch (e) {
					// eslint-disable-next-line no-console
					console.error('error ', e);
					return false;
				} finally {
					commit('appState/setLoading', { wishlistAddItem: false }, { root: true });
				}
			},
			async [actionType.REMOVE_ITEM_FROM_WISHLIST]({ commit }, id: number): Promise<boolean> {
				try {
					commit('appState/setLoading', { wishlistRemoveItem: true }, { root: true });
					const res = await this.$api.wishlist.removeWishlistItem.execute({ wishlistItemId: id });
					if (res.isSuccess) {
						const value = res.getValue();
						if (value?.success) {
							commit(MutationType.REMOVE_ITEM_FROM_WISHLIST, id);
						}
						return value?.success;
					}
					return res.isSuccess;
				} catch (e) {
					// eslint-disable-next-line no-console
					console.error('error ', e);
					return false;
				} finally {
					commit('appState/setLoading', { wishlistRemoveItem: false }, { root: true });
				}
			},
			async [actionType.CLEAR_WISHLIST]({ commit }): Promise<boolean> {
				try {
					commit('appState/setLoading', { wishlistClear: true }, { root: true });
					const res = await this.$api.wishlist.clearWishlist.execute({});
					if (res.isSuccess) {
						const value = res.getValue();
						if (value?.success) {
							commit(MutationType.SET_WISHLIST_ITEMS, []);
						}
						return value?.success;
					}
					return res.isSuccess;
				} catch (e) {
					// eslint-disable-next-line no-console
					console.error('error ', e);
					return false;
				} finally {
					commit('appState/setLoading', { wishlistClear: false }, { root: true });
				}
			},
		},
		mutations: {
			[MutationType.SET_LOADING]: (state, { key, value }: { key: keyof ILoadingState; value: boolean }) => {
				state.loading[key] = value;
			},
			[MutationType.SET_WISHLIST_ITEMS]: (state, items: IWishlistItem[]) => {
				state.items = items;
			},
			[MutationType.ADD_TO_WISHLIST]: (state, item: IWishlistItem) => {
				state.items.push(item);
			},
			[MutationType.REMOVE_ITEM_FROM_WISHLIST]: (state, id: number) => {
				const index = state.items.findIndex((i) => i.id === id);
				if (index > -1) {
					state.items.splice(index, 1);
				}
			},
		},
		getters: {
			items: ({ items }) => items,
			wishlistCount: ({ items }) => items.length,
			loading: ({ loading }) => loading,
			isInWishlist:
				({ items }) =>
				(id: number) =>
					items.some((i) => i.id === id),
		},
	};
}
